/**
 * Five artifacts, one per method stage: a brief, a system sketch, an interface,
 * an automation path, a measurement.
 *
 * The first version of these was pure hairline wireframe on paper, and it read
 * as an unfinished placeholder rather than as a drawing. Every one now carries
 * a header, filled blocks that stand in for real content, labelled parts, and
 * exactly one cobalt element that says what the stage is FOR. Nothing here
 * pretends to be a screenshot of a real client system, because there is no such
 * screenshot to show yet.
 *
 * Design system: paper, ink, cobalt only. Radius 0. No gradients, no shadows.
 */

const W = 360;
const H = 232;

/* Every artifact sits in the same frame with the same header strip, so the
   five of them read as one set rather than five separate drawings. */
const Frame = ({ title, meta, children, label }) => (
  <svg className="artifact" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={label} focusable="false">
    <rect x="0.5" y="0.5" width={W - 1} height={H - 1} fill="none" stroke="rgba(22,19,15,0.26)" />
    <line x1="0" y1="27" x2={W} y2="27" stroke="rgba(22,19,15,0.26)" />
    <text className="artifact__title" x="12" y="18">{title}</text>
    <text className="artifact__meta" x={W - 12} y="18" textAnchor="end">{meta}</text>
    {children}
  </svg>
);

/* Repeated ruled line, used as stand-in body copy. */
const Rule = ({ x, y, w, o = 0.3 }) => (
  <rect x={x} y={y} width={w} height="3" fill={`rgba(22,19,15,${o})`} />
);

/* 1. UNDERSTAND — a brief. Notes taken from watching the work, with the one
      line that turned out to be the real problem pulled out in the margin. */
export const BriefArtifact = () => (
  <Frame
    title="DISCOVERY NOTES"
    meta="DAY 1"
    label="A written brief: notes from watching the work, with the real problem marked in the margin"
  >
    <line x1="42" y1="27" x2="42" y2={H} stroke="rgba(22,19,15,0.16)" />
    {[46, 62, 78].map((y, i) => (
      <text key={y} className="artifact__num" x="30" y={y + 4} textAnchor="end">{`0${i + 1}`}</text>
    ))}
    <Rule x={56} y={44} w={232} />
    <Rule x={56} y={60} w={268} />
    <Rule x={56} y={76} w={186} />

    {/* The line that matters. */}
    <rect x="48" y="92" width={W - 72} height="30" fill="rgba(43,63,240,0.07)" stroke="var(--cobalt)" />
    <Rule x={56} y={100} w={214} o={0.62} />
    <Rule x={56} y={110} w={148} o={0.42} />
    <text className="artifact__tag artifact__tag--key" x="56" y="138">THE ACTUAL PROBLEM</text>

    <Rule x={56} y={156} w={252} />
    <Rule x={56} y={172} w={198} />
    <Rule x={56} y={188} w={228} />
    <Rule x={56} y={204} w={124} />
  </Frame>
);

/* 2. DESIGN — the same work redrawn as a system, with the route through it
      named. Five steps, one decision, one path picked out. */
export const SystemArtifact = () => {
  const box = (x, y, w = 68, h = 34) => ({ x, y, w, h });
  const boxes = [
    { ...box(22, 52), t: "INTAKE" },
    { ...box(146, 52), t: "CHECK" },
    { ...box(270, 52), t: "QUEUE" },
    { ...box(84, 138), t: "DO" },
    { ...box(208, 138), t: "CLOSE" },
  ];
  return (
    <Frame title="SYSTEM SKETCH" meta="5 STEPS" label="A system sketch: the same work drawn as five connected steps with one route picked out">
      {boxes.map((b, i) => (
        <g key={b.t}>
          <rect
            x={b.x} y={b.y} width={b.w} height={b.h}
            fill={i === 1 ? "rgba(43,63,240,0.07)" : "rgba(22,19,15,0.04)"}
            stroke={i === 1 ? "var(--cobalt)" : "rgba(22,19,15,0.42)"}
          />
          <text className="artifact__tag" x={b.x + b.w / 2} y={b.y + b.h / 2 + 3} textAnchor="middle">{b.t}</text>
        </g>
      ))}
      <path className="artifact__line" d="M90 69 H146" markerEnd="url(#ar-ink)" />
      <path className="artifact__key" d="M214 69 H270" markerEnd="url(#ar-key)" />
      <path className="artifact__key" d="M180 86 V112 H118 V138" markerEnd="url(#ar-key)" />
      <path className="artifact__line" d="M152 155 H208" markerEnd="url(#ar-ink)" />
      <defs>
        <marker id="ar-ink" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
          <path d="M0 0 L7 3.5 L0 7 z" fill="rgba(22,19,15,0.42)" />
        </marker>
        <marker id="ar-key" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
          <path d="M0 0 L7 3.5 L0 7 z" fill="var(--cobalt)" />
        </marker>
      </defs>
      <text className="artifact__meta" x="22" y={H - 14}>ONE ROUTE THROUGH, NAMED</text>
    </Frame>
  );
};

/* 3. BUILD — a working screen. A list of jobs, their state, and the one
      action the person on the floor actually presses. */
export const InterfaceArtifact = () => {
  const rows = [
    ["#1187", "OVERDUE", 0.55],
    ["#1188", "IN PROGRESS", 0.4],
    ["#1189", "IN PROGRESS", 0.4],
    ["#1190", "SCHEDULED", 0.3],
  ];
  return (
    <Frame title="JOBS" meta="4 OPEN" label="A working screen: a list of jobs with their state and one clear action">
      <rect x="0" y="27" width="86" height={H - 27} fill="rgba(22,19,15,0.035)" />
      <line x1="86" y1="27" x2="86" y2={H} stroke="rgba(22,19,15,0.16)" />
      {["TODAY", "OPEN", "DONE", "ALL"].map((t, i) => (
        <g key={t}>
          {i === 1 ? <rect x="0" y={44 + i * 26} width="86" height="20" fill="rgba(43,63,240,0.08)" /> : null}
          <text className={`artifact__tag ${i === 1 ? "artifact__tag--key" : ""}`} x="14" y={58 + i * 26}>{t}</text>
        </g>
      ))}

      <rect x="98" y="40" width={W - 110} height="18" fill="rgba(22,19,15,0.06)" />
      <text className="artifact__tag" x="106" y="53">JOB</text>
      <text className="artifact__tag" x="214" y="53">STATE</text>

      {rows.map(([id, state, o], i) => {
        const y = 68 + i * 26;
        return (
          <g key={id}>
            <line x1="98" y1={y + 18} x2={W - 12} y2={y + 18} stroke="rgba(22,19,15,0.14)" />
            <text className="artifact__tag" x="106" y={y + 12}>{id}</text>
            <rect x="214" y={y + 4} width={state === "IN PROGRESS" ? 62 : 44} height="11" fill={`rgba(22,19,15,${o * 0.18})`} />
            <text className="artifact__tag" x="219" y={y + 13}>{state}</text>
          </g>
        );
      })}

      <rect x={W - 104} y={H - 36} width="92" height="24" fill="var(--cobalt)" />
      <text className="artifact__tag artifact__tag--onkey" x={W - 58} y={H - 20} textAnchor="middle">SEND REMINDER</text>
    </Frame>
  );
};

/* 4. AUTOMATE — what now happens without anybody doing it. One trigger, one
      check, and the two things that follow. */
export const FlowArtifact = () => (
  <Frame title="AUTOMATION" meta="RUNS DAILY" label="An automation path: one trigger, a check, and the two things that follow">
    <rect x="16" y="88" width="76" height="32" fill="rgba(43,63,240,0.07)" stroke="var(--cobalt)" />
    <text className="artifact__tag artifact__tag--key" x="54" y="107" textAnchor="middle">INVOICE DUE</text>
    <text className="artifact__meta" x="54" y="80" textAnchor="middle">TRIGGER</text>

    <path className="artifact__key" d="M92 104 H126" />
    <path className="artifact__line" d="M126 104 L162 80 L198 104 L162 128 Z" fill="rgba(22,19,15,0.04)" />
    <text className="artifact__tag" x="162" y="107" textAnchor="middle">PAID?</text>

    <path className="artifact__line" d="M198 104 H226 V56 H258" />
    <path className="artifact__key" d="M198 104 H226 V152 H258" />
    <text className="artifact__meta" x="230" y="48">NO</text>
    <text className="artifact__meta artifact__meta--key" x="230" y="146">YES</text>

    <rect x="258" y="42" width="86" height="28" fill="rgba(22,19,15,0.04)" stroke="rgba(22,19,15,0.42)" />
    <text className="artifact__tag" x="301" y="60" textAnchor="middle">REMIND</text>

    <rect x="258" y="138" width="86" height="28" fill="rgba(22,19,15,0.04)" stroke="var(--cobalt)" />
    <text className="artifact__tag artifact__tag--key" x="301" y="156" textAnchor="middle">CLOSE + LOG</text>

    <text className="artifact__meta" x="16" y={H - 14}>NOBODY OPENS A SPREADSHEET</text>
  </Frame>
);

/* 5. IMPROVE — the number that says whether any of it worked. Hours per week
      on the job, before and after, against the target. */
export const MetricArtifact = () => {
  const pts = [
    [56, 62], [94, 70], [132, 64], [170, 104], [208, 142], [246, 150], [284, 148], [322, 149],
  ];
  const line = pts.map((p, i) => `${i ? "L" : "M"}${p[0]} ${p[1]}`).join(" ");
  return (
    <Frame title="HOURS PER WEEK" meta="ON THIS JOB" label="A measurement: hours per week spent on the job, before and after, against the target">
      <line x1="44" y1="176" x2={W - 14} y2="176" stroke="rgba(22,19,15,0.3)" />
      <line x1="44" y1="42" x2="44" y2="176" stroke="rgba(22,19,15,0.3)" />
      {[["14", 56], ["7", 112], ["0", 172]].map(([t, y]) => (
        <g key={t}>
          <line x1="40" y1={y} x2="44" y2={y} stroke="rgba(22,19,15,0.3)" />
          <text className="artifact__num" x="34" y={y + 4} textAnchor="end">{t}</text>
        </g>
      ))}

      {/* Where the system went in. Everything right of this line is the after. */}
      <line x1="170" y1="42" x2="170" y2="176" stroke="rgba(22,19,15,0.22)" strokeDasharray="3 3" />
      <text className="artifact__meta" x="176" y="52">SYSTEM LIVE</text>

      <path className="artifact__line" d={`M56 62 L94 70 L132 64 L170 104`} />
      <path className="artifact__key" d={`M170 104 L208 142 L246 150 L284 148 L322 149`} />
      <path d={`${line} L322 176 L56 176 Z`} fill="rgba(43,63,240,0.05)" stroke="none" />
      <circle className="artifact__keyfill" cx="322" cy="149" r="4" />

      <text className="artifact__meta" x="56" y={H - 14}>BEFORE</text>
      <text className="artifact__meta artifact__meta--key" x={W - 14} y={H - 14} textAnchor="end">HELD, NOT DRIFTING</text>
    </Frame>
  );
};
